/* eslint-disable import-path/forbidden */

import React from 'react';
import { makeStyles, useTheme } from 'react-scale/components/ui/styles';
import { ScaleAITheme } from 'react-scale/components/ui/theme';
import SvgUpload from 'react-scale/components/icons/Upload';
import { useDropzone } from 'react-dropzone';
import { Tooltip } from 'react-scale/components/design-system';

const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 400;

interface IDropZoneProps {
  width?: number | string;
  height?: number | string;
  handleDrop: (files: File[]) => void;
  disabled?: boolean;
  dropzoneParams?: Record<string, any>;
  file?: File;
  overrideLabel?: string;
  tooltipLabel?: string;
}

interface IStyleProps {
  theme: ScaleAITheme;
  isDragActive: boolean;
  width: number | string;
  height: number | string;
  disabled?: boolean;
}

const useStyles = makeStyles({
  container: (props: IStyleProps) => ({
    backgroundColor: props.theme.palette.background.one,
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: props.isDragActive ? props.theme.palette.text.blue : 'transparent',
    borderWidth: '2px',
    borderStyle: 'dashed',
    width: props.width,
    height: props.height,
    cursor: 'pointer',
    pointerEvents: props.disabled ? 'none' : 'auto',
  }),
  icon: (props: IStyleProps) => ({
    fontSize: '11pt',
    color: props.theme.palette.text.blue,
  }),
  label: (props: IStyleProps) => ({
    color: props.theme.palette.text.blue,
    userSelect: 'none',
  }),
});

const DropZone: React.FC<IDropZoneProps> = ({
  width,
  height,
  handleDrop,
  disabled,
  dropzoneParams,
  file,
  overrideLabel,
  tooltipLabel,
}: IDropZoneProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    ...dropzoneParams,
  });

  const theme = useTheme();
  const classes = useStyles({
    theme,
    isDragActive,
    width: width || DEFAULT_WIDTH,
    height: height || DEFAULT_HEIGHT,
    disabled,
  });
  const dragInactiveLabel = overrideLabel ?? 'Click or drag files to begin upload...';
  const labelText = isDragActive ? 'Drop to upload!' : dragInactiveLabel;
  return (
    <div className="DropZone">
      {file && <label>Attached: {file.name}</label>}
      <div className={classes.container} {...getRootProps()}>
        <input {...getInputProps()} />
        <SvgUpload className={classes.icon} width={40} height={40} fill="currentColor" />
        {tooltipLabel ? (
          <Tooltip title={tooltipLabel}>
            <p className={classes.label}>{labelText}</p>
          </Tooltip>
        ) : (
          <p className={classes.label}>{labelText}</p>
        )}
      </div>
    </div>
  );
};

export default DropZone;
